import React, { useState, useEffect } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Files, Rocket, Code, Palette, ShoppingCart, Globe, Users, Clock, CheckCircle, Plus, Copy, Download, Star, Search, Heart, Share2, TrendingUp, Sparkles, Upload, Eye, BarChart3, Zap, Crown, Lock, Unlock,
  Bot, Wand2, Brain, MessageSquare, Lightbulb, Target, Workflow, GitBranch, Package, ShoppingBag, DollarSign, CreditCard, UserPlus, MessageCircle, History, Settings, RefreshCw, Import, Export, Sliders, HelpCircle
} from 'lucide-react';
import { ProjectStatus, ProjectRole, Permission, TaskStatus, TaskPriority } from '@/types';
import { toast } from 'sonner';
import { AITemplateChat } from './AITemplateChat';

interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  category: 'development' | 'design' | 'marketing' | 'business';
  icon: React.ReactNode;
  color: string;
  estimatedDuration: number;
  complexity: 'simple' | 'medium' | 'complex';
  tags: string[];
  usageCount: number;
  rating: number;
  version: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
    verified: boolean;
  };
  isPublic: boolean;
  isPremium: boolean;
  isFavorited: boolean;
  isCustom: boolean;
  createdAt: Date;
  updatedAt: Date;
  downloads: number;
  successRate: number; // percentage of projects completed using this template
  avgCompletionTime: number; // days
  reviews: {
    count: number;
    average: number;
    distribution: { [key: number]: number }; // rating -> count
  };
  analytics: {
    weeklyUsage: number[];
    popularTags: string[];
    completionRate: number;
    userSatisfaction: number;
  };
  permissions: {
    canEdit: boolean;
    canShare: boolean;
    canDelete: boolean;
    canClone: boolean;
  };
  variants: {
    id: string;
    name: string;
    description: string;
    changes: string[];
  }[];
  tasks: {
    title: string;
    description: string;
    status: TaskStatus;
    priority: TaskPriority;
    estimatedHours: number;
    dependencies: string[];
    tags: string[];
    isOptional: boolean;
    automationTriggers: string[];
    customFields: { [key: string]: any };
  }[];
  milestones: {
    name: string;
    description: string;
    dueDate: number;
    criteria: string[];
    deliverables: string[];
  }[];
  customization: {
    allowTaskModification: boolean;
    allowMilestoneChanges: boolean;
    requiredFields: string[];
    optionalFields: string[];
  };
}

const projectTemplates: ProjectTemplate[] = [
  {
    id: 'web-app',
    name: 'Aplicação Web',
    description: 'Template completo para desenvolvimento de aplicações web modernas',
    category: 'development',
    icon: <Code className="w-6 h-6" />,
    color: 'bg-blue-500',
    estimatedDuration: 45,
    complexity: 'complex',
    tags: ['React', 'Frontend', 'Backend', 'Database'],
    usageCount: 156,
    rating: 4.8,
    version: '3.2.1',
    author: {
      id: 'steercode-official',
      name: 'SteerCode Team',
      verified: true
    },
    isPublic: true,
    isPremium: false,
    isFavorited: false,
    isCustom: false,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-12-01'),
    downloads: 2341,
    successRate: 87,
    avgCompletionTime: 42,
    reviews: {
      count: 89,
      average: 4.8,
      distribution: { 5: 71, 4: 15, 3: 3, 2: 0, 1: 0 }
    },
    analytics: {
      weeklyUsage: [23, 31, 28, 35, 42, 38, 45],
      popularTags: ['React', 'TypeScript', 'TailwindCSS'],
      completionRate: 87,
      userSatisfaction: 4.8
    },
    permissions: {
      canEdit: false,
      canShare: true,
      canDelete: false,
      canClone: true
    },
    variants: [
      {
        id: 'web-app-nextjs',
        name: 'Next.js Variant',
        description: 'Versão otimizada para Next.js',
        changes: ['Next.js framework', 'SSR setup', 'API routes']
      },
      {
        id: 'web-app-minimal',
        name: 'Versão Minimalista',
        description: 'Setup básico sem complexidade',
        changes: ['Menos dependências', 'Setup simplificado']
      }
    ],
    customization: {
      allowTaskModification: true,
      allowMilestoneChanges: true,
      requiredFields: ['name', 'description'],
      optionalFields: ['team', 'budget', 'client']
    },
    tasks: [
      {
        title: 'Configuração do ambiente de desenvolvimento',
        description: 'Instalar dependências, configurar webpack, eslint, prettier',
        status: TaskStatus.TODO,
        priority: TaskPriority.HIGH,
        estimatedHours: 8,
        dependencies: [],
        tags: ['setup', 'config'],
        isOptional: false,
        automationTriggers: ['project_created'],
        customFields: { difficulty: 'medium', skills: ['React', 'Webpack'] }
      },
      {
        title: 'Implementar autenticação de usuário',
        description: 'Sistema de login, registro e recuperação de senha',
        status: TaskStatus.TODO,
        priority: TaskPriority.HIGH,
        estimatedHours: 16,
        dependencies: ['setup'],
        tags: ['auth', 'security'],
        isOptional: false,
        automationTriggers: ['setup_completed'],
        customFields: { difficulty: 'high', skills: ['Auth', 'Security'] }
      },
      {
        title: 'Desenvolver interface principal',
        description: 'Layout responsivo, componentes reutilizáveis',
        status: TaskStatus.TODO,
        priority: TaskPriority.MEDIUM,
        estimatedHours: 24,
        dependencies: ['auth'],
        tags: ['ui', 'frontend'],
        isOptional: false,
        automationTriggers: ['auth_completed'],
        customFields: { difficulty: 'medium', skills: ['React', 'CSS'] }
      },
      {
        title: 'Integração com API',
        description: 'Conectar frontend com backend, gerenciar estado',
        status: TaskStatus.TODO,
        priority: TaskPriority.MEDIUM,
        estimatedHours: 20,
        dependencies: ['ui'],
        tags: ['api', 'integration'],
        isOptional: false,
        automationTriggers: ['ui_completed'],
        customFields: { difficulty: 'high', skills: ['API', 'State Management'] }
      },
      {
        title: 'Testes e otimização',
        description: 'Testes unitários, integração e performance',
        status: TaskStatus.TODO,
        priority: TaskPriority.LOW,
        estimatedHours: 12,
        dependencies: ['api'],
        tags: ['testing', 'optimization'],
        isOptional: true,
        automationTriggers: ['api_completed'],
        customFields: { difficulty: 'medium', skills: ['Testing', 'Performance'] }
      }
    ],
    milestones: [
      { 
        name: 'Setup Completo', 
        description: 'Ambiente configurado', 
        dueDate: 7,
        criteria: ['Dependências instaladas', 'Config files criados', 'Git configurado'],
        deliverables: ['package.json', 'webpack.config.js', '.eslintrc']
      },
      { 
        name: 'MVP', 
        description: 'Versão mínima funcional', 
        dueDate: 21,
        criteria: ['Auth funcional', 'UI básica', 'Navegação'],
        deliverables: ['Login screen', 'Dashboard', 'Navigation']
      },
      { 
        name: 'Beta', 
        description: 'Versão beta para testes', 
        dueDate: 35,
        criteria: ['Todas features', 'Testes básicos', 'Documentação'],
        deliverables: ['App funcional', 'Test suite', 'README']
      },
      { 
        name: 'Launch', 
        description: 'Lançamento oficial', 
        dueDate: 45,
        criteria: ['Performance otimizada', 'Testes completos', 'Deploy'],
        deliverables: ['Production build', 'Deployment', 'Monitoring']
      }
    ]
  },
  {
    id: 'ai-startup',
    name: 'AI Startup MVP',
    description: 'Template premium para startups de IA com integração GPT e machine learning',
    category: 'development',
    icon: <Sparkles className="w-6 h-6" />,
    color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    estimatedDuration: 60,
    complexity: 'complex',
    tags: ['AI', 'Machine Learning', 'GPT', 'Python', 'React'],
    usageCount: 23,
    rating: 4.9,
    version: '1.0.0',
    author: {
      id: 'ai-expert',
      name: 'AI Solutions Pro',
      verified: true
    },
    isPublic: true,
    isPremium: true,
    isFavorited: true,
    isCustom: false,
    createdAt: new Date('2024-11-01'),
    updatedAt: new Date('2024-12-15'),
    downloads: 89,
    successRate: 94,
    avgCompletionTime: 58,
    reviews: {
      count: 12,
      average: 4.9,
      distribution: { 5: 11, 4: 1, 3: 0, 2: 0, 1: 0 }
    },
    analytics: {
      weeklyUsage: [3, 5, 8, 12, 15, 18, 23],
      popularTags: ['OpenAI', 'Python', 'FastAPI'],
      completionRate: 94,
      userSatisfaction: 4.9
    },
    permissions: {
      canEdit: false,
      canShare: true,
      canDelete: false,
      canClone: true
    },
    variants: [
      {
        id: 'ai-saas',
        name: 'SaaS Version',
        description: 'Versão SaaS com billing',
        changes: ['Stripe integration', 'Multi-tenant', 'Subscription model']
      }
    ],
    customization: {
      allowTaskModification: true,
      allowMilestoneChanges: false,
      requiredFields: ['name', 'description', 'ai_model'],
      optionalFields: ['budget', 'team_size']
    },
    tasks: [
      {
        title: 'Setup AI Environment',
        description: 'Configurar ambiente Python, APIs de IA e dependências',
        status: TaskStatus.TODO,
        priority: TaskPriority.HIGH,
        estimatedHours: 12,
        dependencies: [],
        tags: ['python', 'ai', 'setup'],
        isOptional: false,
        automationTriggers: ['project_created'],
        customFields: { difficulty: 'high', skills: ['Python', 'AI/ML'] }
      }
    ],
    milestones: [
      { 
        name: 'AI Setup', 
        description: 'Ambiente AI configurado', 
        dueDate: 10,
        criteria: ['Python env', 'API keys', 'Model access'],
        deliverables: ['requirements.txt', 'API config', 'Model tests']
      }
    ]
  },
  {
    id: 'user-custom-1',
    name: 'Meu Template Personalizado',
    description: 'Template customizado baseado em projetos anteriores bem-sucedidos',
    category: 'business',
    icon: <Crown className="w-6 h-6" />,
    color: 'bg-amber-500',
    estimatedDuration: 30,
    complexity: 'medium',
    tags: ['Custom', 'Business', 'Workflow'],
    usageCount: 5,
    rating: 5.0,
    version: '1.0.0',
    author: {
      id: 'current-user',
      name: 'Leandro',
      verified: false
    },
    isPublic: false,
    isPremium: false,
    isFavorited: true,
    isCustom: true,
    createdAt: new Date('2024-12-10'),
    updatedAt: new Date('2024-12-15'),
    downloads: 0,
    successRate: 100,
    avgCompletionTime: 28,
    reviews: {
      count: 1,
      average: 5.0,
      distribution: { 5: 1, 4: 0, 3: 0, 2: 0, 1: 0 }
    },
    analytics: {
      weeklyUsage: [0, 1, 2, 1, 3, 2, 5],
      popularTags: ['Custom', 'Workflow'],
      completionRate: 100,
      userSatisfaction: 5.0
    },
    permissions: {
      canEdit: true,
      canShare: true,
      canDelete: true,
      canClone: true
    },
    variants: [],
    customization: {
      allowTaskModification: true,
      allowMilestoneChanges: true,
      requiredFields: ['name'],
      optionalFields: ['description', 'team', 'budget', 'client', 'priority']
    },
    tasks: [
      {
        title: 'Definir escopo do projeto',
        description: 'Reunir com stakeholders e definir objetivos',
        status: TaskStatus.TODO,
        priority: TaskPriority.HIGH,
        estimatedHours: 4,
        dependencies: [],
        tags: ['planning', 'stakeholders'],
        isOptional: false,
        automationTriggers: ['project_created'],
        customFields: { difficulty: 'low', skills: ['Communication'] }
      }
    ],
    milestones: [
      { 
        name: 'Kickoff', 
        description: 'Projeto iniciado', 
        dueDate: 3,
        criteria: ['Escopo definido', 'Equipe alocada'],
        deliverables: ['Project charter', 'Team assignments']
      }
    ]
  }
];

export const ProjectTemplates: React.FC = () => {
  const { addProject } = useProject();
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCustomTemplateDialogOpen, setIsCustomTemplateDialogOpen] = useState(false);
  const [isTemplateAnalyticsOpen, setIsTemplateAnalyticsOpen] = useState(false);
  // NEW: AI Builder and advanced features state
  const [isAIBuilderOpen, setIsAIBuilderOpen] = useState(false);
  const [isMarketplaceOpen, setIsMarketplaceOpen] = useState(false);
  const [isCollaborationOpen, setIsCollaborationOpen] = useState(false);
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);
  const [aiBuilderPrompt, setAiBuilderPrompt] = useState('');
  const [aiGeneratedTemplate, setAiGeneratedTemplate] = useState<ProjectTemplate | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating' | 'usage'>('popular');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showPremiumOnly, setShowPremiumOnly] = useState(false);
  const [selectedTemplateForAnalytics, setSelectedTemplateForAnalytics] = useState<ProjectTemplate | null>(null);

  // NEW: Custom template creation state
  const [customTemplate, setCustomTemplate] = useState({
    name: '',
    description: '',
    category: 'business' as const,
    estimatedDuration: 30,
    complexity: 'medium' as const,
    tags: [] as string[],
    isPublic: false
  });

  const categories = [
    { id: 'all', label: 'Todos', icon: <Files className="w-4 h-4" />, count: projectTemplates.length },
    { id: 'development', label: 'Desenvolvimento', icon: <Code className="w-4 h-4" />, count: projectTemplates.filter(t => t.category === 'development').length },
    { id: 'design', label: 'Design', icon: <Palette className="w-4 h-4" />, count: projectTemplates.filter(t => t.category === 'design').length },
    { id: 'marketing', label: 'Marketing', icon: <Globe className="w-4 h-4" />, count: projectTemplates.filter(t => t.category === 'marketing').length },
    { id: 'business', label: 'Negócios', icon: <ShoppingCart className="w-4 h-4" />, count: projectTemplates.filter(t => t.category === 'business').length },
    { id: 'custom', label: 'Meus Templates', icon: <Crown className="w-4 h-4" />, count: projectTemplates.filter(t => t.isCustom).length }
  ];

  const filteredTemplates = projectTemplates
    .filter(template => {
      const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           template.author.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = selectedCategory === 'all' || 
                             (selectedCategory === 'custom' ? template.isCustom : template.category === selectedCategory);

      const matchesFavorites = !showFavoritesOnly || template.isFavorited;
      const matchesPremium = !showPremiumOnly || template.isPremium;

      return matchesSearch && matchesCategory && matchesFavorites && matchesPremium;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.usageCount - a.usageCount;
        case 'newest':
          return b.updatedAt.getTime() - a.updatedAt.getTime();
        case 'rating':
          return b.rating - a.rating;
        case 'usage':
          return b.downloads - a.downloads;
        default:
          return 0;
      }
    });

  // FAVORITOS PERSISTENTES
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        return JSON.parse(localStorage.getItem('favoriteTemplateIds') || '[]');
      } catch {
        return [];
      }
    }
    return [];
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('favoriteTemplateIds', JSON.stringify(favoriteIds));
    }
  }, [favoriteIds]);

  // Update favorite status for rendering
  const templatesToRender = filteredTemplates.map(t => ({
    ...t,
    isFavorited: favoriteIds.includes(t.id)
  }));
  const handleUseTemplate = (template: ProjectTemplate) => {
    setSelectedTemplate(template);
    setProjectName(template.name);
    setIsCreateDialogOpen(true);
  };

  // Atualiza favoritos
  const handleToggleFavorite = (templateId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setFavoriteIds(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
    toast.success('Favorito atualizado!');
  };

  const handleCloneTemplate = (template: ProjectTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    // Create a custom version of the template
    const clonedTemplate = {
      ...template,
      id: `custom-${Date.now()}`,
      name: `${template.name} (Cópia)`,
      isCustom: true,
      isPublic: false,
      author: {
        id: user!.id,
        name: user!.name,
        verified: false
      },
      version: '1.0.0',
      usageCount: 0,
      downloads: 0
    };
    toast.success('Template clonado com sucesso!');
    console.log('Cloned template:', clonedTemplate);
  };

  const handleShareTemplate = (template: ProjectTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: template.name,
        text: template.description,
        url: `${window.location.origin}/templates/${template.id}`
      });
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/templates/${template.id}`);
      toast.success('Link copiado para a área de transferência!');
    }
  };

  const handleViewAnalytics = (template: ProjectTemplate, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedTemplateForAnalytics(template);
    setIsTemplateAnalyticsOpen(true);
  };
  const handleCreateProject = () => {
    if (!selectedTemplate || !projectName.trim()) {
      toast.error('Nome do projeto é obrigatório');
      return;
    }

    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + selectedTemplate.estimatedDuration);

    const newProject = {
      name: projectName,
      description: selectedTemplate.description,
      startDate,
      endDate,
      estimatedDuration: selectedTemplate.estimatedDuration,
      status: ProjectStatus.PLANNING,
      progress: 0,
      members: [
        {
          userId: user!.id,
          role: ProjectRole.OWNER,
          permissions: [
            Permission.CREATE_TASKS,
            Permission.EDIT_TASKS,
            Permission.DELETE_TASKS,
            Permission.ASSIGN_TASKS,
            Permission.VIEW_REPORTS,
            Permission.MANAGE_MEMBERS
          ],
          joinedAt: new Date()
        }
      ],
      createdBy: user!.id,
      templateId: selectedTemplate.id
    };

    addProject(newProject);

    // Update template usage stats
    const updatedTemplate = { 
      ...selectedTemplate, 
      usageCount: selectedTemplate.usageCount + 1 
    };
    console.log('Updated template usage:', updatedTemplate.usageCount);
    toast.success(`Projeto criado com sucesso usando o template "${selectedTemplate.name}"!`);
    setIsCreateDialogOpen(false);
    setSelectedTemplate(null);
    setProjectName('');
  };

  const handleCreateCustomTemplate = () => {
    const newTemplate: ProjectTemplate = {
      id: `custom-${Date.now()}`,
      ...customTemplate,
      icon: <Crown className="w-6 h-6" />,
      color: 'bg-amber-500',
      usageCount: 0,
      rating: 0,
      version: '1.0.0',
      author: {
        id: user!.id,
        name: user!.name,
        verified: false
      },
      isPremium: false,
      isFavorited: false,
      isCustom: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      downloads: 0,
      successRate: 0,
      avgCompletionTime: customTemplate.estimatedDuration,
      reviews: { count: 0, average: 0, distribution: {} },
      analytics: {
        weeklyUsage: [0, 0, 0, 0, 0, 0, 0],
        popularTags: customTemplate.tags,
        completionRate: 0,
        userSatisfaction: 0
      },
      permissions: {
        canEdit: true,
        canShare: true,
        canDelete: true,
        canClone: true
      },
      variants: [],
      customization: {
        allowTaskModification: true,
        allowMilestoneChanges: true,
        requiredFields: ['name', 'description'],
        optionalFields: ['team', 'budget']
      },
      tasks: [
        {
          title: 'Tarefa inicial',
          description: 'Primeira tarefa do projeto',
          status: TaskStatus.TODO,
          priority: TaskPriority.MEDIUM,
          estimatedHours: 8,
          dependencies: [],
          tags: [],
          isOptional: false,
          automationTriggers: ['project_created'],
          customFields: {}
        }
      ],
      milestones: [
        {
          name: 'Primeiro Marco',
          description: 'Primeiro milestone do projeto',
          dueDate: Math.floor(customTemplate.estimatedDuration / 3),
          criteria: ['Critério 1', 'Critério 2'],
          deliverables: ['Entregável 1']
        }
      ]
    };

    console.log('Created custom template:', newTemplate);
    toast.success('Template personalizado criado com sucesso!');
    // Reset form
    setCustomTemplate({
      name: '',
      description: '',
      category: 'business',
      estimatedDuration: 30,
      complexity: 'medium',
      tags: [],
      isPublic: false
    });
    setIsCustomTemplateDialogOpen(false);
  };

  // NEW: AI Template Builder Functions
  // Removed old AI Builder dialog and replaced with new interactive chat component below

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            Templates de Projeto
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">
              {filteredTemplates.length} disponíveis
            </Badge>
          </h1>
          <p className="text-gray-600 mt-1">
            Acelere seu desenvolvimento com templates inteligentes
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              setAiBuilderPrompt('');
              setIsAIBuilderOpen(true);
            }} 
            className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200"
          >
            <Bot className="w-4 h-4 mr-2" />
            IA Builder Chat
          </Button>
          <Button variant="outline" onClick={() => setIsMarketplaceOpen(true)}>
            <ShoppingBag className="w-4 h-4 mr-2" />
            Marketplace
          </Button>
          <Button variant="outline" onClick={() => setIsImportExportOpen(true)}>
            <Import className="w-4 h-4 mr-2" />
            Import/Export
          </Button>
          <Button variant="outline" onClick={() => setIsCustomTemplateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Criar Personalizado
          </Button>
        </div>
      </div>

      {/* AI Chat Feature Highlight */}
      <Card className="bg-gradient-to-r from-purple-50 via-blue-50 to-cyan-50 border-2 border-purple-200 overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white flex-shrink-0">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-gray-900 mb-1">💬 Chat Iterativo com IA</h3>
              <p className="text-sm text-gray-700">
                Novo! Converse com nossa IA para refinar seu template progressivamente. A IA faz perguntas inteligentes e aprende sobre seu projeto em tempo real.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-semibold text-gray-800">Inteligente</span>
              </div>
              <p className="text-xs text-gray-600">Detecta tecnologias automaticamente</p>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-gray-800">Progressivo</span>
              </div>
              <p className="text-xs text-gray-600">Refina template a cada resposta</p>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Lightbulb className="w-4 h-4 text-yellow-600" />
                <span className="text-xs font-semibold text-gray-800">Insightful</span>
              </div>
              <p className="text-xs text-gray-600">Identifica riscos e dependências</p>
            </div>
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-4 h-4 text-orange-600" />
                <span className="text-xs font-semibold text-gray-800">Rápido</span>
              </div>
              <p className="text-xs text-gray-600">Template pronto em minutos</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={() => {
                setAiBuilderPrompt('');
                setIsAIBuilderOpen(true);
              }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Iniciar Chat com IA
            </Button>
            <Button variant="outline" className="text-gray-700">
              <HelpCircle className="w-4 h-4 mr-2" />
              Ver Exemplos
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* AI Recommendations Banner */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">🎯 Recomendações Inteligentes com IA</h3>
              <p className="text-sm text-gray-600">Nosso AI Builder analisa sua descrição e cria templates personalizados automaticamente</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Smart Recommendations Banner */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">🎯 Recomendações Inteligentes</h3>
              <p className="text-sm text-gray-600">IA analisa seu histórico e sugere os melhores templates para você</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Recommended templates based on user history */}
            {projectTemplates.filter(t => ['web-app', 'ai-startup', 'user-custom-1'].includes(t.id)).map(template => (
              <Card key={template.id} className="bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border border-emerald-200">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1 rounded ${template.color} text-white`}>
                      {template.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{template.name}</div>
                      <div className="text-xs text-emerald-600">97% compatibilidade</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-800">
                      Recomendado
                    </Badge>
                    <Button size="sm" variant="outline" className="text-xs h-6">
                      Usar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Pesquisar templates por nome, descrição, tags ou autor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="favorites-only" 
                  checked={showFavoritesOnly}
                  onCheckedChange={setShowFavoritesOnly}
                />
                <Label htmlFor="favorites-only" className="text-sm">Apenas Favoritos</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch 
                  id="premium-only" 
                  checked={showPremiumOnly}
                  onCheckedChange={setShowPremiumOnly}
                />
                <Label htmlFor="premium-only" className="text-sm">Apenas Premium</Label>
              </div>
              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Mais Popular</SelectItem>
                  <SelectItem value="newest">Mais Recente</SelectItem>
                  <SelectItem value="rating">Melhor Avaliado</SelectItem>
                  <SelectItem value="usage">Mais Usado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <div className="flex flex-wrap gap-2">
        {categories.map(category => (
          <Button
            key={category.id}
            variant={selectedCategory === category.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(category.id)}
            className="flex items-center gap-2"
          >
            {category.icon}
            {category.label}
            <Badge variant="secondary" className="ml-1">
              {category.count}
            </Badge>
          </Button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
        {templatesToRender.map(template => (
          <Card 
            key={template.id} 
            className="hover:shadow-xl transition-all duration-200 cursor-pointer group border-2 hover:border-blue-200"
            onClick={() => handleUseTemplate(template)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  <div className={`p-3 rounded-xl ${template.color} text-white shadow-lg`}>
                    {template.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                        {template.name}
                      </CardTitle>
                      {template.author.verified && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verificado
                        </Badge>
                      )}
                      {template.isPremium && (
                        <Badge variant="default" className="text-xs bg-gradient-to-r from-yellow-400 to-orange-400 text-white">
                          <Crown className="w-3 h-3 mr-1" />
                          Premium
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="secondary" className={`text-xs ${
                        template.complexity === 'simple' ? 'bg-green-100 text-green-800' :
                        template.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {template.complexity === 'simple' ? 'Simples' : 
                         template.complexity === 'medium' ? 'Médio' : 'Complexo'}
                      </Badge>
                      <div className="flex items-center space-x-1">
                        <Star className="w-3 h-3 text-yellow-500 fill-current" />
                        <span className="text-xs font-medium">{template.rating.toFixed(1)}</span>
                        <span className="text-xs text-gray-500">({template.reviews.count})</span>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleToggleFavorite(template.id, e)}
                  className={`${template.isFavorited ? 'text-red-500' : 'text-gray-400'} hover:text-red-500`}
                >
                  <Heart className={`w-4 h-4 ${template.isFavorited ? 'fill-current' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>⏱️ {template.estimatedDuration} dias</span>
                  <span>📊 {template.successRate}% sucesso</span>
                  <span>⬇️ {template.downloads}</span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {template.tags.slice(0, 3).map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {template.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{template.tags.length - 3}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center text-xs text-gray-500">
                  <span className="flex items-center">
                    👤 {template.author.name}
                    {template.author.verified && <CheckCircle className="w-3 h-3 ml-1 text-blue-500" />}
                  </span>
                  <span className="mx-2">•</span>
                  <span>v{template.version}</span>
                </div>
              </div>

              <div className="flex space-x-2 pt-2 border-t">
                <Button className="flex-1" onClick={(e) => { e.stopPropagation(); handleUseTemplate(template); }}>
                  <Rocket className="w-4 h-4 mr-2" />
                  Usar Template
                </Button>
                <Button variant="outline" size="sm" onClick={(e) => handleCloneTemplate(template, e)}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={(e) => handleShareTemplate(template, e)}>
                  <Share2 className="w-4 h-4" />
                </Button>
                {(template.usageCount > 10 || template.isPremium) && (
                  <Button variant="outline" size="sm" onClick={(e) => handleViewAnalytics(template, e)}>
                    <BarChart3 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {templatesToRender.length === 0 && (
        <Card className="p-12 text-center">
          <CardContent>
            <div className="text-gray-400 mb-4">
              <Search className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum template encontrado</h3>
            <p className="text-gray-600 mb-4">
              Tente ajustar os filtros ou usar o AI Builder para criar um novo template
            </p>
            <Button onClick={() => setIsAIBuilderOpen(true)} className="bg-purple-600 hover:bg-purple-700">
              <Bot className="w-4 h-4 mr-2" />
              Criar com IA
            </Button>
          </CardContent>
        </Card>
      )}

      {/* AI Builder Dialog - UPDATED with Interactive Chat */}
      <Dialog open={isAIBuilderOpen} onOpenChange={setIsAIBuilderOpen}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              IA Template Builder - Chat Iterativo
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden">
            <AITemplateChat
              initialPrompt={aiBuilderPrompt}
              onTemplateGenerated={(template) => {
                setAiGeneratedTemplate({
                  id: `ai-generated-${Date.now()}`,
                  name: template.projectType,
                  description: `Template gerado pela IA para ${template.projectType}`,
                  category: 'development',
                  icon: <Bot className="w-6 h-6" />,
                  color: 'bg-gradient-to-r from-purple-500 to-blue-500',
                  estimatedDuration: template.estimatedDuration,
                  complexity: template.complexity,
                  tags: template.technologies,
                  usageCount: 0,
                  rating: 0,
                  version: '1.0.0',
                  author: {
                    id: 'ai-assistant',
                    name: 'SteerCode AI',
                    verified: true
                  },
                  isPublic: false,
                  isPremium: false,
                  isFavorited: false,
                  isCustom: true,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  downloads: 0,
                  successRate: 0,
                  avgCompletionTime: template.estimatedDuration,
                  reviews: { count: 0, average: 0, distribution: {} },
                  analytics: {
                    weeklyUsage: [0, 0, 0, 0, 0, 0, 0],
                    popularTags: template.technologies,
                    completionRate: 0,
                    userSatisfaction: 0
                  },
                  permissions: {
                    canEdit: true,
                    canShare: true,
                    canDelete: true,
                    canClone: true
                  },
                  variants: [],
                  customization: {
                    allowTaskModification: true,
                    allowMilestoneChanges: true,
                    requiredFields: ['name', 'description'],
                    optionalFields: ['team', 'budget', 'timeline']
                  },
                  tasks: [
                    {
                      title: 'Configuração inicial do projeto',
                      description: 'Setup baseado na análise de IA',
                      status: TaskStatus.TODO,
                      priority: TaskPriority.HIGH,
                      estimatedHours: 8,
                      dependencies: [],
                      tags: ['setup'],
                      isOptional: false,
                      automationTriggers: ['project_created'],
                      customFields: { aiGenerated: true }
                    },
                    ...template.mainFeatures.map((feature, idx) => ({
                      title: `Implementar: ${feature}`,
                      description: `Develop ${feature} feature`,
                      status: TaskStatus.TODO,
                      priority: idx === 0 ? TaskPriority.HIGH : TaskPriority.MEDIUM,
                      estimatedHours: 16,
                      dependencies: idx === 0 ? [] : ['setup'],
                      tags: ['development'],
                      isOptional: false,
                      automationTriggers: [] as string[],
                      customFields: { aiGenerated: true, feature }
                    }))
                  ] as any[],
                  milestones: template.milestones.map((milestone, idx) => ({
                    name: milestone,
                    description: `Milestone: ${milestone}`,
                    dueDate: Math.ceil((template.estimatedDuration / template.milestones.length) * (idx + 1)),
                    criteria: ['Requirements met', 'Testing completed'],
                    deliverables: ['Code', 'Documentation']
                  }))
                });
                toast.success('Template gerado pela IA com sucesso!');
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-5 h-5" />
              Criar Projeto com Template
            </DialogTitle>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-6">
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${selectedTemplate.color} text-white`}>
                      {selectedTemplate.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{selectedTemplate.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{selectedTemplate.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label htmlFor="projectName">Nome do Projeto *</Label>
                <Input
                  id="projectName"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Digite o nome do seu projeto"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateProject} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Projeto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Advanced Marketplace Dialog */}
      <Dialog open={isMarketplaceOpen} onOpenChange={setIsMarketplaceOpen}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Marketplace de Templates da Comunidade
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Marketplace Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">1,247</div>
                  <div className="text-sm text-blue-800">Templates Disponíveis</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">89k</div>
                  <div className="text-sm text-green-800">Downloads Totais</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">4.7★</div>
                  <div className="text-sm text-purple-800">Avaliação Média</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">523</div>
                  <div className="text-sm text-amber-800">Criadores Ativos</div>
                </CardContent>
              </Card>
            </div>

            {/* Featured Templates */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Templates em Destaque
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projectTemplates.slice(0, 6).map(template => (
                  <Card key={template.id} className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-blue-200">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${template.color} text-white`}>
                            {template.icon}
                          </div>
                          <div>
                            <CardTitle className="text-sm">{template.name}</CardTitle>
                            <div className="flex items-center space-x-1 mt-1">
                              <Star className="w-3 h-3 text-yellow-500 fill-current" />
                              <span className="text-xs">{template.rating.toFixed(1)}</span>
                              <span className="text-xs text-gray-500">({template.reviews.count})</span>
                            </div>
                          </div>
                        </div>
                        {template.isPremium && (
                          <Badge variant="default" className="text-xs bg-gradient-to-r from-yellow-400 to-orange-400">
                            <Crown className="w-3 h-3 mr-1" />
                            Premium
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-xs text-gray-600 mb-2 line-clamp-2">{template.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          ⬇️ {template.downloads} • ⏱️ {template.estimatedDuration}d
                        </div>
                        <Button size="sm" variant="outline" className="text-xs">
                          <Eye className="w-3 h-3 mr-1" />
                          Ver Mais
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Template Categories in Marketplace */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Explorar por Categoria</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4 text-center">
                    <Code className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <div className="font-medium">Desenvolvimento</div>
                    <div className="text-sm text-gray-500">342 templates</div>
                  </CardContent>
                </Card>
                <Card className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4 text-center">
                    <Palette className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                    <div className="font-medium">Design</div>
                    <div className="text-sm text-gray-500">128 templates</div>
                  </CardContent>
                </Card>
                <Card className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4 text-center">
                    <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <div className="font-medium">E-commerce</div>
                    <div className="text-sm text-gray-500">89 templates</div>
                  </CardContent>
                </Card>
                <Card className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4 text-center">
                    <Bot className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                    <div className="font-medium">IA & ML</div>
                    <div className="text-sm text-gray-500">45 templates</div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Collaboration Section */}
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">🤝 Colaboração em Templates</h3>
                    <p className="text-sm text-gray-600">Trabalhe em equipe na criação e melhoria de templates</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <UserPlus className="w-4 h-4 text-indigo-600" />
                      <span className="text-sm font-medium">Convite Colaboradores</span>
                    </div>
                    <p className="text-xs text-gray-600">Convide membros da equipe para editar</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <GitBranch className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium">Controle de Versão</span>
                    </div>
                    <p className="text-xs text-gray-600">Histórico completo de mudanças</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageCircle className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium">Comentários</span>
                    </div>
                    <p className="text-xs text-gray-600">Discussões em tempo real</p>
                  </div>
                </div>
                <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700" onClick={() => setIsCollaborationOpen(true)}>
                  <Users className="w-4 h-4 mr-2" />
                  Iniciar Colaboração
                </Button>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Collaboration Dialog */}
      <Dialog open={isCollaborationOpen} onOpenChange={setIsCollaborationOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Colaboração em Templates
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-800">Trabalho Colaborativo</span>
                </div>
                <p className="text-sm text-blue-700">
                  Convide sua equipe para trabalhar juntos na criação e melhoria de templates de projeto.
                </p>
              </CardContent>
            </Card>

            <Tabs defaultValue="invite" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="invite">Convidar Colaboradores</TabsTrigger>
                <TabsTrigger value="permissions">Permissões</TabsTrigger>
                <TabsTrigger value="activity">Atividade</TabsTrigger>
              </TabsList>

              <TabsContent value="invite" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="collaborator-email">Email do Colaborador</Label>
                  <div className="flex gap-2">
                    <Input
                      id="collaborator-email"
                      placeholder="usuario@exemplo.com"
                      className="flex-1"
                    />
                    <Select defaultValue="editor">
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">Visualizador</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Convidar
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium">Colaboradores Ativos</h4>
                  <div className="space-y-2">
                    {[
                      { name: 'Ana Silva', email: 'ana@empresa.com', role: 'Editor', status: 'online' },
                      { name: 'Carlos Santos', email: 'carlos@empresa.com', role: 'Admin', status: 'offline' },
                      { name: 'Maria Costa', email: 'maria@empresa.com', role: 'Visualizador', status: 'online' }
                    ].map((collaborator, index) => (
                      <Card key={index}>
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-medium">
                              {collaborator.name.charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{collaborator.name}</div>
                              <div className="text-xs text-gray-500">{collaborator.email}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant={collaborator.status === 'online' ? 'default' : 'secondary'}>
                              {collaborator.role}
                            </Badge>
                            <div className={`w-2 h-2 rounded-full ${
                              collaborator.status === 'online' ? 'bg-green-500' : 'bg-gray-400'
                            }`} />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="permissions" className="space-y-4">
                <div className="space-y-4">
                  <h4 className="font-medium">Configurar Permissões por Função</h4>

                  {[
                    {
                      role: 'Admin',
                      permissions: ['Editar template', 'Convidar colaboradores', 'Gerenciar permissões', 'Excluir template'],
                      color: 'bg-red-100 text-red-800'
                    },
                    {
                      role: 'Editor',
                      permissions: ['Editar template', 'Comentar', 'Visualizar histórico'],
                      color: 'bg-blue-100 text-blue-800'
                    },
                    {
                      role: 'Visualizador',
                      permissions: ['Visualizar template', 'Comentar', 'Clonar template'],
                      color: 'bg-gray-100 text-gray-800'
                    }
                  ].map((roleConfig, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <Badge className={roleConfig.color}>{roleConfig.role}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {roleConfig.permissions.map((permission, permIndex) => (
                            <div key={permIndex} className="flex items-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm">{permission}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="activity" className="space-y-4">
                <div className="space-y-3">
                  <h4 className="font-medium">Atividade Recente</h4>
                  <div className="space-y-3">
                    {[
                      { user: 'Ana Silva', action: 'editou as tarefas do template', time: '2 horas atrás', type: 'edit' },
                      { user: 'Carlos Santos', action: 'adicionou um novo marco', time: '5 horas atrás', type: 'add' },
                      { user: 'Você', action: 'criou o template', time: '1 dia atrás', type: 'create' },
                      { user: 'Maria Costa', action: 'comentou sobre a complexidade', time: '2 dias atrás', type: 'comment' }
                    ].map((activity, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className={`w-2 h-2 rounded-full ${
                          activity.type === 'edit' ? 'bg-blue-500' :
                          activity.type === 'add' ? 'bg-green-500' :
                          activity.type === 'create' ? 'bg-purple-500' : 'bg-orange-500'
                        }`} />
                        <div className="flex-1">
                          <div className="text-sm">
                            <span className="font-medium">{activity.user}</span> {activity.action}
                          </div>
                          <div className="text-xs text-gray-500">{activity.time}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import/Export Dialog */}
      <Dialog open={isImportExportOpen} onOpenChange={setIsImportExportOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Import className="w-5 h-5" />
              Import/Export Avançado
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="import" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="import">Importar Templates</TabsTrigger>
              <TabsTrigger value="export">Exportar Templates</TabsTrigger>
            </TabsList>

            <TabsContent value="import" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: 'Jira', icon: '🔷', description: 'Importar projetos do Jira como templates' },
                  { name: 'Trello', icon: '📋', description: 'Converter boards do Trello em templates' },
                  { name: 'Asana', icon: '🎯', description: 'Importar projetos do Asana' },
                  { name: 'Notion', icon: '📝', description: 'Converter páginas do Notion' },
                  { name: 'Monday.com', icon: '📊', description: 'Importar workflows do Monday' },
                  { name: 'CSV/Excel', icon: '📈', description: 'Importar de planilhas' }
                ].map((platform, index) => (
                  <Card key={index} className="cursor-pointer hover:shadow-md transition-all border-2 hover:border-blue-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl mb-2">{platform.icon}</div>
                      <div className="font-medium mb-1">{platform.name}</div>
                      <div className="text-xs text-gray-600 mb-3">{platform.description}</div>
                      <Button size="sm" variant="outline" className="w-full">
                        <Import className="w-3 h-3 mr-2" />
                        Conectar
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">Import Inteligente</span>
                  </div>
                  <p className="text-sm text-blue-700">
                    Nossa IA analisa automaticamente a estrutura dos seus projetos existentes e cria templates otimizados.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="export" className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Selecionar Templates para Exportar</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {projectTemplates.slice(0, 4).map(template => (
                      <div key={template.id} className="flex items-center space-x-2 p-3 border rounded-lg">
                        <input type="checkbox" id={template.id} className="rounded" />
                        <div className={`p-1 rounded ${template.color} text-white`}>
                          {template.icon}
                        </div>
                        <Label htmlFor={template.id} className="flex-1 cursor-pointer">
                          {template.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { format: 'JSON', icon: '📄', description: 'Formato nativo do SteerCode' },
                    { format: 'CSV', icon: '📊', description: 'Para Excel e Google Sheets' },
                    { format: 'PDF', icon: '📑', description: 'Documentação visual' }
                  ].map((format, index) => (
                    <Card key={index} className="cursor-pointer hover:shadow-md transition-all">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl mb-2">{format.icon}</div>
                        <div className="font-medium mb-1">{format.format}</div>
                        <div className="text-xs text-gray-600 mb-3">{format.description}</div>
                        <Button size="sm" variant="outline" className="w-full">
                          <Download className="w-3 h-3 mr-2" />
                          Exportar
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Template Analytics Dialog */}
      <Dialog open={isTemplateAnalyticsOpen} onOpenChange={setIsTemplateAnalyticsOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Analytics do Template: {selectedTemplateForAnalytics?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedTemplateForAnalytics && (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{selectedTemplateForAnalytics.usageCount}</div>
                    <div className="text-sm text-gray-600">Usos Totais</div>
                    <div className="text-xs text-green-600">+23% este mês</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{selectedTemplateForAnalytics.successRate}%</div>
                    <div className="text-sm text-gray-600">Taxa de Sucesso</div>
                    <div className="text-xs text-green-600">+5% este mês</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{selectedTemplateForAnalytics.avgCompletionTime}d</div>
                    <div className="text-sm text-gray-600">Tempo Médio</div>
                    <div className="text-xs text-red-600">-2d este mês</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{selectedTemplateForAnalytics.rating.toFixed(1)}★</div>
                    <div className="text-sm text-gray-600">Avaliação</div>
                    <div className="text-xs text-green-600">+0.2 este mês</div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="usage" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="usage">Uso & Performance</TabsTrigger>
                  <TabsTrigger value="reviews">Avaliações</TabsTrigger>
                  <TabsTrigger value="tasks">Análise de Tarefas</TabsTrigger>
                  <TabsTrigger value="trends">Tendências</TabsTrigger>
                </TabsList>

                <TabsContent value="usage" className="space-y-6">
                  {/* Usage Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Uso Semanal</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <div className="text-sm text-gray-500">Gráfico de uso semanal</div>
                          <div className="text-xs text-gray-400">
                            Semanas: {selectedTemplateForAnalytics.analytics.weeklyUsage.join(', ')} usos
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Performance Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Métricas de Performance</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Taxa de Conclusão</span>
                            <span className="text-sm font-medium">{selectedTemplateForAnalytics.analytics.completionRate}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ width: `${selectedTemplateForAnalytics.analytics.completionRate}%` }}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Satisfação do Usuário</span>
                            <span className="text-sm font-medium">{selectedTemplateForAnalytics.analytics.userSatisfaction.toFixed(1)}/5</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ width: `${(selectedTemplateForAnalytics.analytics.userSatisfaction / 5) * 100}%` }}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Eficiência vs Estimativa</span>
                            <span className="text-sm font-medium text-green-600">+12%</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            Projetos são concluídos 12% mais rápido que o estimado
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Segmentação de Usuários</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[
                          { segment: 'Desenvolvedores Sênior', percentage: 45, color: 'bg-blue-500' },
                          { segment: 'Equipes Pequenas (2-5)', percentage: 35, color: 'bg-green-500' },
                          { segment: 'Startups', percentage: 28, color: 'bg-purple-500' },
                          { segment: 'Empresas Grandes', percentage: 15, color: 'bg-orange-500' }
                        ].map((segment, index) => (
                          <div key={index} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{segment.segment}</span>
                              <span>{segment.percentage}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`${segment.color} h-2 rounded-full`}
                                style={{ width: `${segment.percentage}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="reviews" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Rating Distribution */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Distribuição de Avaliações</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[5, 4, 3, 2, 1].map(rating => {
                          const count = selectedTemplateForAnalytics.reviews.distribution[rating] || 0;
                          const percentage = selectedTemplateForAnalytics.reviews.count > 0 
                            ? (count / selectedTemplateForAnalytics.reviews.count) * 100 
                            : 0;
                          return (
                            <div key={rating} className="flex items-center space-x-2">
                              <span className="text-sm w-3">{rating}</span>
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-yellow-500 h-2 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className="text-sm w-8">{count}</span>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>

                    {/* Recent Reviews */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Avaliações Recentes</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {[
                          { user: 'Ana Silva', rating: 5, comment: 'Template excelente! Economizou muito tempo.', date: '2 dias atrás' },
                          { user: 'Carlos M.', rating: 4, comment: 'Muito útil, poderia ter mais detalhes nas tarefas.', date: '1 semana atrás' },
                          { user: 'Maria K.', rating: 5, comment: 'Perfeito para projetos web modernos.', date: '2 semanas atrás' }
                        ].map((review, index) => (
                          <div key={index} className="border-b last:border-b-0 pb-3 last:pb-0">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-medium text-sm">{review.user}</div>
                              <div className="flex items-center space-x-1">
                                {Array.from({ length: review.rating }).map((_, i) => (
                                  <Star key={i} className="w-3 h-3 text-yellow-500 fill-current" />
                                ))}
                                <span className="text-xs text-gray-500 ml-2">{review.date}</span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600">{review.comment}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="tasks" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Análise de Tarefas do Template</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {selectedTemplateForAnalytics.tasks.map((task, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-medium">{task.title}</div>
                              <Badge variant="outline">{task.estimatedHours}h estimadas</Badge>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                              <div className="text-center">
                                <div className="text-sm text-gray-600">Tempo Real Médio</div>
                                <div className="text-lg font-bold text-blue-600">{task.estimatedHours * 0.9}h</div>
                                <div className="text-xs text-green-600">10% mais rápido</div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-gray-600">Taxa de Conclusão</div>
                                <div className="text-lg font-bold text-green-600">94%</div>
                                <div className="text-xs text-green-600">Excelente</div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-gray-600">Dificuldade Percebida</div>
                                <div className="text-lg font-bold text-orange-600">
                                  {task.priority === TaskPriority.HIGH ? 'Alta' : 
                                   task.priority === TaskPriority.MEDIUM ? 'Média' : 'Baixa'}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="trends" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Tendências de Uso</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {[
                          { trend: 'Crescimento Mensal', value: '+23%', color: 'text-green-600', icon: TrendingUp },
                          { trend: 'Tempo de Setup', value: '-15%', color: 'text-green-600', icon: Clock },
                          { trend: 'Taxa de Abandono', value: '-8%', color: 'text-green-600', icon: CheckCircle },
                          { trend: 'Satisfação Usuário', value: '+0.3', color: 'text-green-600', icon: Star }
                        ].map((trend, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <trend.icon className="w-5 h-5 text-gray-600" />
                              <span className="text-sm">{trend.trend}</span>
                            </div>
                            <span className={`text-sm font-bold ${trend.color}`}>{trend.value}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Insights Automáticos</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {[
                          { 
                            insight: 'Este template tem alta taxa de sucesso em equipes pequenas', 
                            type: 'success',
                            icon: CheckCircle
                          },
                          { 
                            insight: 'Usuários completam 12% mais rápido que o estimado', 
                            type: 'info',
                            icon: Clock
                          },
                          { 
                            insight: 'Recomendado para projetos de média complexidade', 
                            type: 'suggestion',
                            icon: Lightbulb
                          },
                          { 
                            insight: 'Popularidade crescendo 23% ao mês', 
                            type: 'trend',
                            icon: TrendingUp
                          }
                        ].map((insight, index) => (
                          <div key={index} className={`p-3 rounded-lg border-l-4 ${
                            insight.type === 'success' ? 'bg-green-50 border-green-500' :
                            insight.type === 'info' ? 'bg-blue-50 border-blue-500' :
                            insight.type === 'suggestion' ? 'bg-yellow-50 border-yellow-500' :
                            'bg-purple-50 border-purple-500'
                          }`}>
                            <div className="flex items-start space-x-2">
                              <insight.icon className={`w-4 h-4 mt-0.5 ${
                                insight.type === 'success' ? 'text-green-600' :
                                insight.type === 'info' ? 'text-blue-600' :
                                insight.type === 'suggestion' ? 'text-yellow-600' :
                                'text-purple-600'
                              }`} />
                              <span className="text-sm text-gray-700">{insight.insight}</span>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};